import { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Download, Music2, FileText } from "lucide-react";
import { api, trackCoverUrl, fmtError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { ReviewForm } from "../components/Reviews";

const Compte = () => {
  const { user, checked } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rated, setRated] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    api
      .get("/purchases")
      .then((r) => setPurchases(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  if (checked && user === false) return <Navigate to="/connexion" replace />;
  if (!user) return null;

  const download = async (t, kind = "audio") => {
    try {
      const url = kind === "pdf" ? `/purchases/${t.track_id}/license.pdf` : `/download/${t.track_id}`;
      const res = await api.get(url, { responseType: "blob" });
      const objUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = kind === "pdf" ? `Licence_${t.title}.pdf` : `${t.title}.wav`;
      a.click();
      URL.revokeObjectURL(objUrl);
      toast.success(kind === "pdf" ? `Certificat de licence « ${t.title} » téléchargé` : `« ${t.title} » téléchargé`);
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  return (
    <main data-testid="account-page" className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.35em] text-amber-500">Mon compte</p>
        <h1 className="mt-3 font-display text-4xl font-black tracking-tighter sm:text-5xl">
          {user.name || user.email}
        </h1>
        <p className="mt-2 text-sm text-zinc-500" data-testid="account-email">{user.email}</p>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-14 flex items-center justify-between border-b border-white/[0.08] pb-4">
          <h2 className="font-display text-2xl font-medium">Mes licences</h2>
          <span className="text-xs uppercase tracking-[0.25em] text-zinc-500" data-testid="purchases-count">
            {purchases.length} morceau{purchases.length > 1 ? "x" : ""}
          </span>
        </div>
      </Reveal>

      {loading ? (
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse border border-white/[0.08] bg-surface" />
          ))}
        </div>
      ) : purchases.length === 0 ? (
        <div className="mt-16 border border-dashed border-white/15 p-16 text-center" data-testid="purchases-empty">
          <Music2 className="mx-auto h-10 w-10 text-zinc-600" />
          <p className="mt-4 text-sm uppercase tracking-[0.25em] text-zinc-500">Aucune licence pour le moment</p>
          <button
            onClick={() => navigate("/catalogue")}
            data-testid="purchases-browse"
            className="mt-6 border border-amber-500/60 px-6 py-3 text-xs uppercase tracking-[0.25em] text-amber-400 transition-colors hover:bg-amber-500 hover:text-black"
          >
            Explorer le catalogue
          </button>
        </div>
      ) : (
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="purchases-grid">
          {purchases.map((t, i) => (
            <Reveal key={t.track_id} delay={i * 0.05}>
              <div className="flex items-center gap-4 border border-white/[0.08] bg-surface p-4" data-testid={`purchase-${t.track_id}`}>
                <img src={trackCoverUrl(t)} alt={t.title} className="h-16 w-16 border border-white/10 object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-base font-medium">{t.title}</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    {(t.usages || []).join(" · ")} —{" "}
                    {t.exclusive ? (
                      <span data-testid={`exclusive-badge-${t.track_id}`} className="font-bold text-amber-400">Licence EXCLUSIVE</span>
                    ) : (
                      "Licence active"
                    )}
                  </p>
                  {rated[t.track_id] ? (
                    <p className="mt-1 text-[10px] text-emerald-400">Merci pour votre avis !</p>
                  ) : (
                    <ReviewForm track={t} onDone={(r) => setRated((prev) => ({ ...prev, [t.track_id]: r }))} />
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => download(t, "pdf")}
                    data-testid={`license-pdf-${t.track_id}`}
                    className="flex h-11 w-11 items-center justify-center border border-white/20 text-zinc-300 transition-colors hover:border-amber-500 hover:text-amber-400"
                    title="Télécharger le certificat de licence PDF"
                  >
                    <FileText className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => download(t)}
                    data-testid={`download-${t.track_id}`}
                    className="flex h-11 w-11 items-center justify-center border border-amber-500/50 text-amber-400 transition-colors hover:bg-amber-500 hover:text-black"
                    title="Télécharger le master"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      )}
      <Reveal>
        <Link
          to="/contact"
          data-testid="compte-contact-link"
          className="mt-10 flex items-center justify-between border border-white/[0.08] bg-surface p-6 transition-colors hover:border-amber-500/50"
        >
          <div>
            <p className="font-display text-lg font-medium">Une question ? Contactez le studio</p>
            <p className="mt-1 text-sm text-zinc-500">Exclusivité, sur-mesure, licence — réponse sous 48 h ouvrées.</p>
          </div>
          <span className="text-amber-500">→</span>
        </Link>
      </Reveal>
    </main>
  );
};

export default Compte;
